import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  Grid,
  Button,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isSameMonth, isSameDay, getDay } from 'date-fns';
import { ReporterAvailability } from '../types/availability';
import { availabilityApi } from '../utils/availabilityApi';
import { HourSlotsEncoder } from '../utils/hourSlotsEncoder';
import DailyAvailabilityEditor from './DailyAvailabilityEditor';

// Custom styled day button
const CalendarDayButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isAvailable' && prop !== 'isPartiallyAvailable' && prop !== 'hasInPerson' && prop !== 'isCurrentMonth'
})<{ 
  isAvailable: boolean; 
  isPartiallyAvailable: boolean; 
  hasInPerson: boolean;
  isCurrentMonth: boolean;
}>(({ theme, isAvailable, isPartiallyAvailable, isCurrentMonth }) => ({
  minWidth: 40,
  minHeight: 40,
  padding: 4,
  margin: 1,
  position: 'relative',
  fontSize: '0.875rem',
  opacity: isCurrentMonth ? 1 : 0.4,
  backgroundColor: isCurrentMonth && isAvailable 
    ? (isPartiallyAvailable ? theme.palette.warning.light : theme.palette.success.light)
    : isCurrentMonth ? theme.palette.grey[100] : 'transparent',
  color: isCurrentMonth && isAvailable 
    ? (isPartiallyAvailable ? theme.palette.warning.contrastText : theme.palette.success.contrastText)
    : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isCurrentMonth 
      ? (isAvailable 
          ? (isPartiallyAvailable ? theme.palette.warning.main : theme.palette.success.main)
          : theme.palette.grey[200])
      : theme.palette.grey[100],
  }
}));

interface AvailabilityCalendarProps {
  onDateSelect?: (date: Date) => void;
  openEditorOnSelect?: boolean;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({ onDateSelect, openEditorOnSelect = true }) => {
  const theme = useTheme();
  const [availability, setAvailability] = useState<ReporterAvailability | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // Load availability data
  const loadAvailability = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await availabilityApi.getAvailability();
      setAvailability(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load availability data');
      console.error('Error loading availability:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvailability();
  }, []);

  // Calendar helper functions
  const getDaysInCalendar = (date: Date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    
    // Get all days in the month, filtering out weekends
    const allMonthDays = eachDayOfInterval({ start, end });
    const monthDays = allMonthDays.filter(day => {
      const dayOfWeek = getDay(day);
      return dayOfWeek !== 0 && dayOfWeek !== 6; // Exclude Sunday (0) and Saturday (6)
    });
    
    // Add padding days from previous and next month to fill grid (weekdays only)
    const paddingStart = [];
    const paddingEnd = [];
    
    // Add previous month weekdays if month doesn't start on Monday
    let paddingDate = new Date(start);
    while (getDay(paddingDate) !== 1) { // Until we reach Monday
      paddingDate.setDate(paddingDate.getDate() - 1);
      const dayOfWeek = getDay(paddingDate);
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Skip weekends
        paddingStart.unshift(new Date(paddingDate));
      }
    }
    
    // Add next month weekdays to complete rows of 5 (Monday-Friday)
    const totalDays = paddingStart.length + monthDays.length;
    const rowsNeeded = Math.ceil(totalDays / 5);
    const totalCells = rowsNeeded * 5;
    
    paddingDate = new Date(end);
    for (let i = 0; i < totalCells - totalDays; i++) {
      paddingDate.setDate(paddingDate.getDate() + 1);
      const dayOfWeek = getDay(paddingDate);
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Skip weekends
        paddingEnd.push(new Date(paddingDate));
      } else {
        i--; // Don't count weekend days toward our target
      }
    }
    
    return [...paddingStart, ...monthDays, ...paddingEnd];
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  // Determine availability status for a given date
  const getDateAvailabilityStatus = (date: Date) => {
    if (!availability) return { isAvailable: false, isPartiallyAvailable: false, hasInPerson: false };

    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check for availability records (handle both date formats)
    const availabilityRecord = availability.availability.find(record => {
      const recordDate = record.availabilityDate.includes('T') ? record.availabilityDate.split('T')[0] : record.availabilityDate;
      return recordDate === dateStr;
    });
    
    if (availabilityRecord) {
      // Use hour slots encoding to determine availability
      const hasAvailability = HourSlotsEncoder.hasAvailability(availabilityRecord.hourSlots);
      const jobTypes = HourSlotsEncoder.getJobTypes(availabilityRecord.hourSlots);
      
      return {
        isAvailable: hasAvailability,
        isPartiallyAvailable: hasAvailability, // Could be enhanced to check for partial hours
        hasInPerson: jobTypes.includes('in-person')
      };
    }

    // No availability record = not available
    return {
      isAvailable: false,
      isPartiallyAvailable: false,
      hasInPerson: false
    };
  };


  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    if (openEditorOnSelect) {
      setEditorOpen(true);
    }
    onDateSelect?.(date);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    // Refresh availability data when editor closes
    loadAvailability();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading availability calendar...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              // Check if the error indicates authentication failure
              error.includes('session has expired') || error.includes('log in') ? (
                <Button 
                  color="inherit" 
                  size="small" 
                  href="/login"
                  sx={{ ml: 2 }}
                >
                  Log In
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Unable to load your availability schedule. Please try refreshing the page.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manage Your Schedule
          </Typography>
          
          {/* Legend */}
          <Box mb={2}>
            <Typography variant="caption" display="block" gutterBottom>
              Calendar Legend:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: theme.palette.success.light,
                    borderRadius: 1,
                    mr: 0.5
                  }}
                />
                <Typography variant="caption">Available</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: theme.palette.warning.light,
                    borderRadius: 1,
                    mr: 0.5
                  }}
                />
                <Typography variant="caption">Partially Available</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: theme.palette.grey[300],
                    borderRadius: 1,
                    mr: 0.5
                  }}
                />
                <Typography variant="caption">Unavailable</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: theme.palette.info.main }} />
                <Typography variant="caption">In-person available</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography variant="caption">*</Typography>
                <Typography variant="caption" sx={{ ml: 0.5 }}>Exception</Typography>
              </Box>
            </Box>
          </Box>

          {/* Custom Calendar Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <IconButton onClick={goToPreviousMonth}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6">
              {format(currentMonth, 'MMMM yyyy')}
            </Typography>
            <IconButton onClick={goToNextMonth}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Calendar Grid */}
          <Paper sx={{ p: 1, mx: 2 }}>
            {/* Day Headers - 5 columns for weekdays */}
            <Grid container spacing={0.5} sx={{ mb: 1 }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day) => (
                <Grid item xs={2.4} key={day}>
                  <Typography 
                    variant="caption" 
                    align="center" 
                    sx={{ display: 'block', fontWeight: 'bold', py: 1 }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Days - 5 columns for weekdays */}
            <Grid container spacing={0.5}>
              {getDaysInCalendar(currentMonth).map((date, index) => {
                const { isAvailable, isPartiallyAvailable, hasInPerson } = getDateAvailabilityStatus(date);
                const isCurrentMonthDay = isSameMonth(date, currentMonth);
                const isSelected = isSameDay(date, selectedDate);

                return (
                  <Grid item xs={2.4} key={index}>
                    <CalendarDayButton
                      variant={isSelected ? "contained" : "text"}
                      onClick={() => handleDateChange(date)}
                      isAvailable={isAvailable}
                      isPartiallyAvailable={isPartiallyAvailable}
                      hasInPerson={hasInPerson}
                      isCurrentMonth={isCurrentMonthDay}
                      fullWidth
                    >
                      {format(date, 'd')}
                      {hasInPerson && isCurrentMonthDay && (
                        <PersonIcon
                          sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            fontSize: '8px',
                            color: theme.palette.info.main
                          }}
                        />
                      )}
                    </CalendarDayButton>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Click on any date to edit your availability for that day.
          </Typography>
        </CardContent>
      </Card>

      {/* Daily availability editor */}
      <DailyAvailabilityEditor
        open={editorOpen}
        selectedDate={selectedDate}
        availability={availability}
        onClose={handleEditorClose}
        onSave={handleEditorClose}
      />
    </>
  );
};

export default AvailabilityCalendar;